import React from 'react';

import type { IdAdvertiserDto } from '@/services/ws-selfbooking/src/main/resources/rest-api';
import { AdvertiserStatus, getAdvertisers } from '@/services/ws-selfbooking/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY } from 'src/constants';
import type { Option } from 'src/types';

type UseAdvertiserSelfbookingOptionsReturn = {
  options: Option<IdAdvertiserDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

type UseAdvertiserSelfbookingOptions = () => UseAdvertiserSelfbookingOptionsReturn;

export const useAdvertiserSelfbookingOptions: UseAdvertiserSelfbookingOptions = () => {
  const [searchFilter, setSearchFilter] = React.useState<string>('');

  const getAdvertiserListRequest = useRequest({
    apiMethod: getAdvertisers,
    params: { filter: searchFilter },
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getAdvertiserListRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item?.name ?? '',
        value: item.id,
        active: item.status === AdvertiserStatus.ACTIVE || item.status === AdvertiserStatus.ADMIN,
      })) ?? [],
  );
  const total = useSubscribe(getAdvertiserListRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getAdvertiserListRequest.pending$);

  return React.useMemo<UseAdvertiserSelfbookingOptionsReturn>(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
